import { useState, useMemo, useCallback } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, addMonths, addDays } from 'date-fns';
import { parseDate } from '@/lib/localizedDateUtils';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, AlertTriangle, ArrowRight, ArrowLeft, FileText, Calendar, DollarSign } from 'lucide-react';
import { 
  useRequestRenegotiation,
  usePaymentAgreementsByDeal,
  useAgreementPaymentSummary,
  useDealOpenBalance,
  getAgreementStatusLabel,
  getAgreementStatusColor,
  getAgreementReasonLabel,
  type AgreementReason,
  type PaymentAgreement,
  type AgreementPaymentSummary,
  type PaymentFrequency,
  type CustomInstallment,
} from '@/hooks/finance/usePaymentAgreements';
import { FrequencySelect, CustomInstallmentsTable, InstallmentsSummary } from './renegotiation';
import { cn } from '@/lib/utils';

// ============ Types ============

const REASONS: { value: NonNullable<AgreementReason>; label: string }[] = [
  { value: 'financial_difficulty', label: 'Dificuldade financeira' },
  { value: 'service_issue', label: 'Problema com serviço' },
  { value: 'other', label: 'Outro' },
];

const formSchema = z.object({
  num_installments: z.coerce.number().int().min(1, 'Mínimo 1 parcela').max(60, 'Máximo 60 parcelas'),
  first_due_date: z.string().min(1, 'Data é obrigatória'),
  reason: z.enum(['financial_difficulty', 'service_issue', 'other']),
  reason_notes: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface RequestRenegotiationModalProps {
  open: boolean;
  onClose: () => void;
  dealId: string;
  clientName: string;
  dealName?: string;
  /** Fallback if query fails */
  currentBalance?: number;
  currentInstallments?: number;
}

// ============ Helpers ============

const formatCurrency = (value: number, currency = 'USD') =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);

const formatDate = (date: string | null) => {
  if (!date) return '-';
  try {
    return safeFormat(new Date(date), 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return date;
  }
};

// ============ Agreement Card Component ============

interface AgreementCardProps {
  agreement: PaymentAgreement;
  isActive: boolean;
  summary?: AgreementPaymentSummary | null;
  isLoadingSummary?: boolean;
}

function AgreementCard({ agreement, isActive, summary, isLoadingSummary }: AgreementCardProps) {
  const isOriginal = agreement.agreement_number === 1;
  const isRejectedOrCancelled = ['rejected', 'cancelled'].includes(agreement.status);
  const installmentAmount = agreement.num_installments > 0 
    ? agreement.agreed_amount / agreement.num_installments 
    : 0;

  return (
    <div className={cn(
      "border rounded-lg p-4 space-y-2",
      isActive ? "border-primary bg-primary/5" : "border-border bg-muted/30",
      isRejectedOrCancelled && "opacity-60"
    )}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isOriginal ? (
            <FileText className="h-4 w-4 text-muted-foreground" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-amber-500" />
          )}
          <span className="font-medium text-sm">
            {isOriginal ? 'ACORDO ORIGINAL' : `${agreement.agreement_number - 1}ª RENEGOCIAÇÃO`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isActive && (
            <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
              ACORDO ATUAL
            </Badge>
          )}
          <Badge 
            variant="outline" 
            className={cn("text-xs", getAgreementStatusColor(agreement.status))}
          >
            {getAgreementStatusLabel(agreement.status)}
          </Badge>
        </div>
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-muted-foreground">Valor:</span>{' '}
          <span className="font-medium">{formatCurrency(agreement.agreed_amount)}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Estrutura:</span>{' '}
          <span className="font-medium">
            {agreement.num_installments}x de {formatCurrency(installmentAmount)}
          </span>
        </div>
        {!isOriginal && agreement.original_balance > 0 && (
          <div className="col-span-2">
            <span className="text-muted-foreground">Saldo renegociado:</span>{' '}
            <span className="font-medium">{formatCurrency(agreement.original_balance)}</span>
          </div>
        )}
        <div>
          <span className="text-muted-foreground">
            {isOriginal ? 'Data início:' : 'Data renegociação:'}
          </span>{' '}
          <span className="font-medium">
            {formatDate(isOriginal ? agreement.first_due_date : agreement.created_at)}
          </span>
        </div>
        {!isOriginal && agreement.reason && (
          <div>
            <span className="text-muted-foreground">Motivo:</span>{' '}
            <span className="font-medium">{getAgreementReasonLabel(agreement.reason)}</span>
          </div>
        )}
      </div>

      {/* Active Agreement Summary */}
      {isActive && (
        <div className="pt-2 mt-2 border-t border-border/50">
          <p className="text-xs font-medium text-muted-foreground mb-2">SITUAÇÃO ATUAL</p>
          {isLoadingSummary ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : summary ? (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Pago:</span>{' '}
                <span className="font-medium text-green-600">
                  {formatCurrency(summary.paidAmount)} ({summary.paidCount} parcela{summary.paidCount !== 1 ? 's' : ''})
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Em aberto:</span>{' '}
                <span className="font-medium text-destructive">
                  {formatCurrency(summary.pendingAmount)} ({summary.pendingCount} parcela{summary.pendingCount !== 1 ? 's' : ''})
                </span>
              </div>
              {summary.lastPaidDate && (
                <div>
                  <span className="text-muted-foreground">Último pgto:</span>{' '}
                  <span className="font-medium">{formatDate(summary.lastPaidDate)}</span>
                </div>
              )}
              {summary.maxDaysOverdue > 0 && (
                <div>
                  <span className="text-muted-foreground">Maior atraso:</span>{' '}
                  <span className="font-medium text-destructive">{summary.maxDaysOverdue} dias</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Sem dados de pagamento</p>
          )}
        </div>
      )}
    </div>
  );
}

// ============ Context Step (Step 1) ============

interface ContextStepProps {
  agreements: PaymentAgreement[];
  activeAgreement: PaymentAgreement | null;
  activeSummary: AgreementPaymentSummary | null;
  isLoadingSummary: boolean;
  clientName: string;
  dealName?: string;
  currentBalance?: number;
  onContinue: () => void;
  onClose: () => void;
}

function ContextStep({
  agreements,
  activeAgreement,
  activeSummary,
  isLoadingSummary,
  clientName,
  dealName,
  currentBalance,
  onContinue,
  onClose,
}: ContextStepProps) {
  // Só conta renegociações que foram efetivamente ativas/concluídas
  const renegotiationCount = agreements.filter(a => 
    a.agreement_number > 1 && 
    ['active', 'completed', 'defaulted', 'superseded'].includes(a.status)
  ).length;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Solicitar Renegociação
        </DialogTitle>
        <DialogDescription>
          Revise o histórico de acordos antes de propor uma nova condição.
        </DialogDescription>
      </DialogHeader>

      {/* Client Info */}
      <div className="p-3 bg-muted rounded-lg">
        <p className="font-medium">{clientName}</p>
        {dealName && <p className="text-sm text-muted-foreground">{dealName}</p>}
      </div>

      {/* Agreements History */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
        <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Histórico de Acordos
        </p>
        {agreements.map((agreement) => (
          <AgreementCard
            key={agreement.id}
            agreement={agreement}
            isActive={activeAgreement?.id === agreement.id}
            summary={activeAgreement?.id === agreement.id ? activeSummary : null}
            isLoadingSummary={activeAgreement?.id === agreement.id ? isLoadingSummary : false}
          />
        ))}
      </div>

      {/* Renegotiation Warning */}
      {renegotiationCount > 0 && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500 flex-shrink-0" />
          <span className="text-sm text-amber-800 dark:text-amber-300">
            Este cliente já renegociou {renegotiationCount} vez{renegotiationCount !== 1 ? 'es' : ''}
          </span>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button 
          type="button" 
          onClick={onContinue}
          disabled={
            (activeSummary?.pendingAmount ?? 0) <= 0 &&
            !(currentBalance && currentBalance > 0)
          }
        >
          Continuar
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </DialogFooter>
    </>
  );
}

// ============ Proposal Step (Step 2) ============

interface ProposalStepProps {
  clientName: string;
  dealName?: string;
  balance: number;
  openCount?: number;
  nextAgreementNumber: number;
  onBack: () => void;
  onSubmit: (data: FormData, frequency: PaymentFrequency, customInstallments: CustomInstallment[] | null) => void;
  isSubmitting: boolean;
}

// Helper to calculate due date based on frequency
const getNextDueDate = (date: Date, frequency: PaymentFrequency): Date => {
  switch (frequency) {
    case 'weekly':
      return addDays(date, 7);
    case 'biweekly':
      return addDays(date, 15);
    case 'monthly':
    default:
      return addMonths(date, 1);
  }
};

// Helper to calculate last due date
const getLastDueDate = (firstDate: string, count: number, frequency: PaymentFrequency): Date => {
  let date = new Date(firstDate);
  for (let i = 1; i < count; i++) {
    date = getNextDueDate(date, frequency);
  }
  return date;
};

function ProposalStep({
  clientName,
  dealName,
  balance,
  openCount,
  nextAgreementNumber,
  onBack,
  onSubmit,
  isSubmitting,
}: ProposalStepProps) {
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [frequency, setFrequency] = useState<PaymentFrequency>('monthly');
  const [customInstallments, setCustomInstallments] = useState<CustomInstallment[]>([]);
  const [hasCustomChanges, setHasCustomChanges] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      num_installments: 6,
      first_due_date: safeFormat(addMonths(new Date(), 1), 'yyyy-MM-dd'),
      reason: 'financial_difficulty',
      reason_notes: '',
    },
  });

  const numInstallments = form.watch('num_installments');
  const firstDueDate = form.watch('first_due_date');
  const installmentAmount = numInstallments > 0 ? balance / numInstallments : 0;
  const lastDueDate = firstDueDate && numInstallments > 0 
    ? getLastDueDate(firstDueDate, numInstallments, frequency)
    : null;

  // Generate installments for custom mode based on simple mode settings
  const generateInstallmentsFromSimple = useCallback((): CustomInstallment[] => {
    const installments: CustomInstallment[] = [];
    const baseAmount = Math.round((balance / numInstallments) * 100) / 100;
    let dueDate = parseDate(firstDueDate) || new Date(firstDueDate);

    for (let i = 0; i < numInstallments; i++) {
      const isLast = i === numInstallments - 1;
      const amount = isLast 
        ? Math.round((balance - baseAmount * (numInstallments - 1)) * 100) / 100
        : baseAmount;

      installments.push({
        amount,
        due_date: safeFormat(dueDate, 'yyyy-MM-dd'),
      });

      dueDate = getNextDueDate(dueDate, frequency);
    }

    return installments;
  }, [balance, numInstallments, firstDueDate, frequency]);

  // Handle entering custom mode
  const handleEnterCustomMode = useCallback(() => {
    const generatedInstallments = generateInstallmentsFromSimple();
    setCustomInstallments(generatedInstallments);
    setHasCustomChanges(false);
    setIsCustomMode(true);
  }, [generateInstallmentsFromSimple]);

  // Handle exiting custom mode
  const handleExitCustomMode = useCallback(() => {
    if (hasCustomChanges) {
      if (!confirm('Deseja descartar as alterações personalizadas?')) {
        return;
      }
    }
    setCustomInstallments([]);
    setIsCustomMode(false);
    setHasCustomChanges(false);
  }, [hasCustomChanges]);

  // Update a custom installment
  const handleUpdateInstallment = useCallback((index: number, field: 'amount' | 'due_date', value: number | string) => {
    setCustomInstallments(prev => {
      const updated = [...prev];
      if (field === 'amount') {
        updated[index] = { ...updated[index], amount: value as number };
      } else {
        updated[index] = { ...updated[index], due_date: value as string };
      }
      return updated;
    });
    setHasCustomChanges(true);
  }, []);

  // Remove an installment
  const handleRemoveInstallment = useCallback((index: number) => {
    setCustomInstallments(prev => prev.filter((_, i) => i !== index));
    setHasCustomChanges(true);
  }, []);

  // Add a new installment
  const handleAddInstallment = useCallback(() => {
    const lastInstallment = customInstallments[customInstallments.length - 1];
    const nextDate = lastInstallment 
      ? safeFormat(getNextDueDate(new Date(lastInstallment.due_date), frequency), 'yyyy-MM-dd')
      : safeFormat(addMonths(new Date(), 1), 'yyyy-MM-dd');
    
    setCustomInstallments(prev => [...prev, { amount: 0, due_date: nextDate }]);
    setHasCustomChanges(true);
  }, [customInstallments, frequency]);

  // Validate custom installments
  const customValidation = useMemo(() => {
    if (!isCustomMode || customInstallments.length === 0) {
      return { errors: [], warnings: [], total: 0, isValid: true };
    }

    const total = customInstallments.reduce((sum, i) => sum + i.amount, 0);
    const difference = Math.round((total - balance) * 100) / 100;
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check sum
    if (Math.abs(difference) > 0.01) {
      errors.push(`Diferença de ${Math.abs(difference).toFixed(2)}`);
    }

    // Check for zero or negative amounts
    if (customInstallments.some(i => i.amount <= 0)) {
      errors.push('Parcela(s) com valor zero ou negativo');
    }

    // Check date order (warnings only)
    for (let i = 1; i < customInstallments.length; i++) {
      if (new Date(customInstallments[i].due_date) <= new Date(customInstallments[i - 1].due_date)) {
        warnings.push(`Parcela ${i + 1} tem data anterior à parcela ${i}`);
      }
    }

    return { errors, warnings, total, isValid: errors.length === 0 };
  }, [isCustomMode, customInstallments, balance]);

  const handleFormSubmit = (data: FormData) => {
    if (isCustomMode) {
      onSubmit(data, frequency, customInstallments);
    } else {
      onSubmit(data, frequency, null);
    }
  };

  const canSubmit = isCustomMode ? customValidation.isValid : true;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Solicitar Renegociação
        </DialogTitle>
        <DialogDescription>
          Propor nova condição de pagamento para aprovação.
        </DialogDescription>
      </DialogHeader>

      {/* Header Info */}
      <div className="space-y-2 p-3 bg-muted rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Cliente:</span>
          <span className="font-medium">{clientName}{dealName ? ` - ${dealName}` : ''}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <DollarSign className="h-3.5 w-3.5" />
            Saldo a renegociar:
          </span>
          <span className="font-semibold text-destructive">
            {formatCurrency(balance)}
            {openCount ? (
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                ({openCount} parcela{openCount !== 1 ? 's' : ''} em aberto)
              </span>
            ) : null}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            Esta será a renegociação:
          </span>
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            {nextAgreementNumber - 1}ª
          </Badge>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {/* New Agreement Section */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              {isCustomMode ? 'Novo Acordo (Personalizado)' : 'Novo Acordo'}
            </p>

            {!isCustomMode && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="num_installments"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nº de Parcelas</FormLabel>
                        <FormControl>
                          <Input type="number" min={1} max={60} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-2">
                    <Label>Frequência</Label>
                    <FrequencySelect value={frequency} onChange={setFrequency} />
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="first_due_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data da 1ª Parcela</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Calculated Summary */}
                <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Valor por parcela:</span>
                    <span className="text-lg font-semibold text-primary">
                      {formatCurrency(installmentAmount)}
                    </span>
                  </div>
                  {lastDueDate && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Última parcela:</span>
                      <span className="text-sm font-medium">
                        {safeFormat(lastDueDate, 'dd/MM/yyyy', { locale: ptBR })}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Custom Mode Table */}
            {isCustomMode && (
              <div className="space-y-3">
                <CustomInstallmentsTable
                  installments={customInstallments}
                  onUpdate={handleUpdateInstallment}
                  onRemove={handleRemoveInstallment}
                  onAdd={handleAddInstallment}
                  currency="USD"
                />
                <InstallmentsSummary
                  totalInstallments={customValidation.total}
                  balance={balance}
                  currency="USD"
                  warnings={customValidation.warnings}
                />
              </div>
            )}

            {/* Toggle Custom Mode */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="custom-mode"
                checked={isCustomMode}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleEnterCustomMode();
                  } else {
                    handleExitCustomMode();
                  }
                }}
              />
              <Label htmlFor="custom-mode" className="text-sm cursor-pointer">
                Personalizar valores ou datas
              </Label>
            </div>
          </div>

          {/* Reason Section */}
          <div className="space-y-3">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Motivo</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {REASONS.map((reason) => (
                        <SelectItem key={reason.value} value={reason.value}>
                          {reason.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reason_notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Justificativa</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Descreva o motivo da renegociação..."
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
            <Button type="submit" disabled={isSubmitting || !canSubmit}>
              {isSubmitting ? 'Enviando...' : 'Solicitar Aprovação'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}

// ============ Main Modal Component ============

export function RequestRenegotiationModal({
  open,
  onClose,
  dealId,
  clientName,
  dealName,
  currentBalance,
  currentInstallments,
}: RequestRenegotiationModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  
  const { data: agreements = [], isLoading: isLoadingAgreements } = usePaymentAgreementsByDeal(dealId);
  const requestMutation = useRequestRenegotiation();

  // Find effective agreement: prioritize 'active', fallback to most recent
  // agreement (even cancelled/defaulted/superseded) so we still show the
  // current payment situation when the original was cancelled but parcels remain open.
  const activeAgreement = useMemo(() => {
    if (agreements.length === 0) return null;
    const active = agreements.find(a => a.status === 'active');
    if (active) return active;
    // pick highest agreement_number (most recent)
    return [...agreements].sort((a, b) => b.agreement_number - a.agreement_number)[0] || null;
  }, [agreements]);

  // Fetch summary for active agreement
  const { 
    data: activeSummary, 
    isLoading: isLoadingSummary 
  } = useAgreementPaymentSummary(activeAgreement?.id || null);

  // Calculate next agreement number
  const nextAgreementNumber = useMemo(() => {
    if (agreements.length === 0) return 2;
    return Math.max(...agreements.map(a => a.agreement_number)) + 1;
  }, [agreements]);

  // Saldo em aberto do deal inteiro (rede de segurança quando o acordo
  // não cobre todas as parcelas ou não traz saldo)
  const { data: dealOpen } = useDealOpenBalance(dealId || null);

  // Use calculated balance or fallback
  const balance =
    (activeSummary?.pendingAmount ?? 0) > 0
      ? activeSummary!.pendingAmount
      : (dealOpen?.openAmount ?? 0) > 0
        ? dealOpen!.openAmount
        : currentBalance ?? 0;

  const openCount =
    (activeSummary?.pendingAmount ?? 0) > 0
      ? activeSummary?.pendingCount
      : dealOpen?.openCount;

  // Reset step when modal opens
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setStep(1);
      onClose();
    }
  };

  const handleSubmit = async (
    data: FormData, 
    frequency: PaymentFrequency, 
    customInstallments: CustomInstallment[] | null
  ) => {
    await requestMutation.mutateAsync({
      deal_id: dealId,
      agreed_amount: balance,
      num_installments: customInstallments ? customInstallments.length : data.num_installments,
      first_due_date: customInstallments ? customInstallments[0].due_date : data.first_due_date,
      reason: data.reason,
      reason_notes: data.reason_notes,
      frequency: customInstallments ? undefined : frequency,
      custom_installments: customInstallments,
    });

    setStep(1);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        {isLoadingAgreements ? (
          <div className="space-y-4 py-6">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : step === 1 ? (
          <ContextStep
            agreements={agreements}
            activeAgreement={activeAgreement}
            activeSummary={activeSummary ?? null}
            isLoadingSummary={isLoadingSummary}
            clientName={clientName}
            dealName={dealName}
            currentBalance={currentBalance}
            onContinue={() => setStep(2)}
            onClose={onClose}
          />
        ) : (
          <ProposalStep
            clientName={clientName}
            dealName={dealName}
            balance={balance}
            openCount={openCount}
            nextAgreementNumber={nextAgreementNumber}
            onBack={() => setStep(1)}
            onSubmit={handleSubmit}
            isSubmitting={requestMutation.isPending}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
